import React from 'react';
import { Server } from '../../server/server';
import AlertModal from '../modals/AlertModal';
import MessageModal from '../modals/MessageModal';
import PageEnvelope from '../elements/PageEnvelope';
import PageHeader from '../elements/PageHeader';
import './SitePage.css';
import QuestionModal from '../modals/QuestionModal';
import UpgradeDetailsModal from '../modals/UpgradeDetailsModal';
import { Navigate } from 'react-router-dom';
import subscriptionsData from '../../data/subscriptions.json';

const FOUNDERS_CLUB_ENABLED = true;

interface Subscription {
  id: string;
  trial: string;
  card: boolean;
  members: number;
  tier: number;
  interval: number;
  price: number;
  public: boolean;
  plan: string;
  name: string;
}

interface SubscriptionsPageState {
  subscription: any | null;
  alert: string;
  message: string;
  loading: boolean;
  selected: string;
  question: string;
  navigate: string;
  upgradeDetails: {
    isOpen: boolean;
    total: number;
    lines: Array<{
      amount: number;
      description: string;
    }>;
    tracking?: {
      rru: number;
      wru: number;
    };
    period?: {
      start: number;
      end: number;
    };
  };
}

class SubscriptionsPage extends React.Component<{}, SubscriptionsPageState> {
  private SUBSCRIPTIONS: Subscription[] = subscriptionsData as Subscription[];

  constructor(props: {}) {
    super(props);
    this.state = {
      subscription: null,
      alert: '',
      message: '',
      loading: false,
      selected: '',
      question: '',
      navigate: '',
      upgradeDetails: {
        isOpen: false,
        total: 0,
        lines: [],
        tracking: undefined,
        period: undefined
      }
    };
  }

  async componentDidMount() {
    this.load();
  }

  async load() {
    this.setState({loading: true});

    await Server.user.sleep(500);
    const response = await Server.user.getCommunitySubscription();
    
    this.setState({loading: false});

    if(response.success) 
      this.setState({subscription: response.subscription});
    else
      this.setState({alert: 'There was an error loading the current subscription.'});
  }

  async handleSubscription(id: string) {
    this.setState({alert: 'Feature coming soon!'});
    return;
    
    let sub = this.state.subscription;
    this.setState({selected: id});

    const newSub = this.SUBSCRIPTIONS.find(s => s.id === id);

    if(sub && sub.id != 'free' && sub.status != 'trialing') {
      // Find current and new subscription details
      const currentSub = this.SUBSCRIPTIONS.find(s => s.id === this.state.subscription.id);
      if(!currentSub || !newSub) {
        this.setState({alert: 'There was an error loading the current or new subscription.'});
        return;
      }

      let isDowngrade = newSub.tier < currentSub.tier || (newSub.tier === currentSub.tier && newSub.interval < currentSub.interval);

      if(sub.schedule && newSub.plan == currentSub.plan) 
        this.setState({question: 'This will cancel the scheduled downgrade and return to the current subscription.  Do you want to continue?'});
      else if(isDowngrade) 
        this.setState({question: `The new subscription will will start on ${this.formatUnixTimestamp(sub.period.end)} after the current billing cycle.  Do you want to continue?`});
      else 
        this.handleUpgrade(id)
    }
    else {
      this.createUpdateSubscription(id);
    }
  }

  async handleUpgrade(id: string) {
    this.setState({ message: 'Getting upgrade amount...' });
    await Server.user.sleep(500);
    let response = await Server.user.getUpgradeCommunitySubscriptionDetails(id);
    this.setState({ message: '' });

    if(response.success) {
      this.setState({
        upgradeDetails: {
          isOpen: true,
          total: response.total,
          lines: response.lines,
          tracking: (response as any).tracking,
          period: (response as any).period
        },
        selected: id
      });
    }
    else
      this.setState({alert: response.message});
  }

  onQuestionYes() {
    this.setState({question: ''});
    this.createUpdateSubscription(this.state.selected);
  }

  onUpgradeConfirm() {
    this.setState({
      upgradeDetails: {
        isOpen: false,
        total: 0,
        lines: [],
        tracking: undefined,
        period: undefined
      }
    });
    this.createUpdateSubscription(this.state.selected);
  }

  onUpgradeCancel() {
    this.setState({
      upgradeDetails: {
        isOpen: false,
        total: 0,
        lines: [],
        tracking: undefined,
        period: undefined
      }
    });
  }

  async createUpdateSubscription(id: string) {
    let createSub = (this.state.subscription == null || this.state.subscription.id == 'free');

    if(createSub) 
      this.setState({ message: 'Preparing subscription...' });
    else
      this.setState({ message: 'Updating subscription...' });

    try {
      const params = {id, successUrl: window.location.href, cancelUrl: window.location.href};

      let response: any = null;
      
      if(createSub)
        response = await Server.user.createCommunitySubscription(params);
      else
        response = await Server.user.updateCommunitySubscription(params);
      if (response.success) {
        if(response.url)
          window.location.href = response.url;
        else {
          setTimeout(() => {
            this.setState({message: ''});
            this.load();
          }, 3000);
        }
      } else {
        // Show error message
        this.setState({ alert: response.message, message: '' });
      }
    } catch (error) {
      console.error('Failed to create subscription:', error);
      this.setState({ 
        message: '',
        alert: 'There was an error processing your subscription request.'
      });
    }
  }

  isTierActive(tierNumber: number): boolean {
    if (!this.state.subscription) return false;
    
    const currentId = this.state.subscription.id;
    if (currentId === 'none') return false;
    
    // Find the current subscription and check if it matches the tier
    const currentSub = this.SUBSCRIPTIONS.find(sub => sub.id === currentId);
    return currentSub ? currentSub.tier === tierNumber : false;
  }

  isIntervalActive(subscription: Subscription): boolean {
    if (!this.state.subscription) return false;
    
    const currentId = this.state.subscription.id;
    if (currentId === 'none') return false;
    
    return currentId === subscription.id;
  }

  hasScheduledChange(): boolean {
    if (!this.state.subscription) return false;
    return this.state.subscription.schedule && this.state.subscription.schedule !== null;
  }

  getScheduledSubscriptionId(): string | null {
    if (!this.hasScheduledChange()) return null;
    return this.state.subscription.schedule;
  }

  isScheduledSubscription(subscription: Subscription): boolean {
    const scheduledId = this.getScheduledSubscriptionId();
    return scheduledId === subscription.id;
  }

  isCurrentSubscriptionScheduled(): boolean {
    if (!this.state.subscription) return false;
    const currentId = this.state.subscription.id;
    if (currentId === 'none') return false;
    
    const scheduledId = this.getScheduledSubscriptionId();
    return scheduledId === currentId;
  }

  formatUnixTimestamp(timestamp: number): string {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString();
  }

  getCurrentSubscriptionName(): string {
    if (!this.state.subscription) return 'None';
    const currentId = this.state.subscription.id;
    
    const currentSub = this.SUBSCRIPTIONS.find(sub => sub.id === currentId);
    if (!currentSub) return 'Unknown';
    
    return currentSub.name;
  }

  getScheduledSubscriptionName(): string | null {
    if (!this.hasScheduledChange()) return null;
    const scheduledId = this.getScheduledSubscriptionId();
    if (!scheduledId) return null;
    
    const scheduledSub = this.SUBSCRIPTIONS.find(sub => sub.id === scheduledId);
    return scheduledSub ? scheduledSub.name : 'Unknown';
  }

  getTierName(tierNumber: number): string {
    const tierNames = ['Micro', 'Starter', 'Founders Club', 'Growth'];
    return tierNames[tierNumber] || 'Unknown';
  }

  getTierSubscriptions(tierNumber: number): Subscription[] {
    return this.SUBSCRIPTIONS.filter(sub => sub.tier === tierNumber);
  }

  getTierDisplayInfo(tierNumber: number) {
    const subscriptions = this.getTierSubscriptions(tierNumber);
    if (subscriptions.length === 0) return null;
    
    const firstSub = subscriptions[0];
    return {
      name: this.getTierName(tierNumber),
      members: firstSub.members,
      public: firstSub.public,
      trialDays: parseInt(firstSub.trial),
      isFounders: tierNumber === 2
    };
  }

  renderSubscriptionTier(tierNumber: number) {
    if(!this.state.subscription)
      return null;

    const tierInfo = this.getTierDisplayInfo(tierNumber);
    if (!tierInfo) return null;

    const sub = this.state.subscription;
    const firstSub = sub.first;
    const isTrialing = sub.status == 'trialing';

    // if(tierInfo.isFounders && !FOUNDERS_CLUB_ENABLED && sub.id.indexOf('founders') == -1)
    //   return null;

    // Hide founders tier if not free or already started
    // if(tierInfo.isFounders && !sub.first)
    //   return null;

    if(tierInfo.isFounders) {
      let showFounders = (sub.id == 'free' && sub.first) || (sub.id != 'free' && isTrialing) || sub.id.indexOf('founders') != -1;
      if(!showFounders)
        return null;
    }

    const subscriptions = this.getTierSubscriptions(tierNumber);
    const monthlySub = subscriptions.find(sub => sub.interval === 0);
    const yearlySub = subscriptions.find(sub => sub.interval === 1);

    return (
      <div key={tierInfo.name} className="site-page-panel" style={{
        border: this.isTierActive(tierNumber) ? '2px solid var(--notification-background-color)' : undefined
      }}>
        <div className="site-page-column">
          <div className="site-page-row" style={{alignItems: 'flex-start'}}>
            {/* First Column - Subscription Details */}
            <div style={{flex: 1}}>
              <div><b>{tierInfo.name}</b></div>
              <div className="site-page-row">
                <div>{tierInfo.members} Members</div>
              </div>
              {tierInfo.public ? (
                <div className="site-page-row">
                  <div>Public or Private</div>
                </div>
              ) : (
                <div className="site-page-row">
                  <div>Private Only</div>
                </div>
              )}
            </div>

            {/* Second Column - Pricing */}
            <div style={{flex: 1, textAlign: 'right'}}>
              {monthlySub && <div className="site-page-title-text">${monthlySub.price}/mo</div>}
              {yearlySub && <div className="site-page-subtext">${yearlySub.price}/yr</div>}
            </div>
          </div>

          {/* Special pricing text for Founders Club */}
          {tierInfo.isFounders && (firstSub || isTrialing) && (
            <div className="site-page-subtext" style={{textAlign: 'center'}}>
              <div>The first 2 years are free!</div>
            </div>
          )}

          {/* Trial period text for paid plans */}
          {!tierInfo.isFounders && (firstSub || isTrialing) && (
            <div className="site-page-subtext" style={{textAlign: 'center'}}>
              {tierInfo.trialDays} day trial period.
            </div>
          )}


          {/* Select Buttons - Centered below columns */}
          <div style={{textAlign: 'center'}}>
            <div style={{display: 'flex', gap: '10px', justifyContent: 'center'}}>
              {monthlySub && (
                <button 
                  disabled={this.isScheduledSubscription(monthlySub) || (this.isIntervalActive(monthlySub) && !this.hasScheduledChange())}
                  onClick={() => this.handleSubscription(monthlySub.id)}
                  style={{
                    border: this.isIntervalActive(monthlySub) || this.isScheduledSubscription(monthlySub)
                      ? `2px solid var(--notification-background-color)` 
                      : undefined
                  }}
                >
                  Monthly
                </button>
              )}
              {yearlySub && (
                <button 
                  disabled={this.isScheduledSubscription(yearlySub) || (this.isIntervalActive(yearlySub) && !this.hasScheduledChange())}
                  onClick={() => this.handleSubscription(yearlySub.id)}
                  style={{
                    border: this.isIntervalActive(yearlySub) || this.isScheduledSubscription(yearlySub)
                      ? `2px solid var(--notification-background-color)` 
                      : undefined
                  }}
                >
                  Yearly
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if(this.state.navigate)
      return <Navigate to={this.state.navigate} />;

    if(this.state.subscription && this.state.subscription.status == 'past_due')
      return <Navigate to="/admin" />;

    return (
      <div className="site-page">
        <PageEnvelope width="600px">
          <div className="site-page-column">
            <PageHeader text={this.state.loading ? 'Loading...' : 'Subscriptions'} navigate="/admin" />

              {!this.state.loading && this.state.subscription && this.state.subscription.id != 'free' && (
                <div className="site-page-notification-panel" style={{
                  textAlign: 'center',
                  padding: '15px',
                  marginTop: '5px'
                }}>
                  {this.state.subscription && this.state.subscription.status === 'trialing' ? (
                    <div>The current subscription is a trial of {this.getCurrentSubscriptionName()} that ends on {this.formatUnixTimestamp(this.state.subscription.trial.end)}.</div>
                  ) : (
                    <div>The current subscription is {this.getCurrentSubscriptionName()}.</div>
                  )}
                  {this.hasScheduledChange() && this.getScheduledSubscriptionName() && (
                    <div style={{marginTop: '5px'}}>
                      The {this.getScheduledSubscriptionName()} subscription starts on {this.formatUnixTimestamp(this.state.subscription.period.end)}.
                    </div>
                  )}
                </div>
              )}

            {!this.state.loading && 
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '15px',
                width: '100%',
                marginTop: '5px'
              }}>
                {this.SUBSCRIPTIONS
                  .filter(sub => sub.id !== 'free')
                  .map(sub => sub.tier)
                  .filter((tier, index, self) => self.indexOf(tier) === index)
                  .sort((a, b) => a - b)
                  .map(tierNumber => this.renderSubscriptionTier(tierNumber))}
              </div>
            }
          </div>
        </PageEnvelope>

        <AlertModal message={this.state.alert} button="OK" onClose={()=>this.setState({alert: ''})}/>
        <QuestionModal message={this.state.question} onYes={()=>this.onQuestionYes()} onNo={()=>this.setState({question: ''})}/>
        <UpgradeDetailsModal 
          isOpen={this.state.upgradeDetails.isOpen}
          onClose={()=>this.onUpgradeCancel()}
          onConfirm={()=>this.onUpgradeConfirm()}
          total={this.state.upgradeDetails.total}
          lines={this.state.upgradeDetails.lines}
          tracking={this.state.upgradeDetails.tracking}
          period={this.state.upgradeDetails.period}
        />
        <MessageModal message={this.state.message} />
      </div>
    );
  }
}

export default SubscriptionsPage;
